import { useQuery, useMutation } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import {
  Card,
  Form,
  Button,
  Spinner,
  InputGroup,
  Dropdown,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useNavigate, useBlocker } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faStore,
  faBuilding,
  faMapMarkerAlt,
  faUser,
  faEye,
  faEyeSlash,
  faLock,
  faUnlock,
  faBriefcase,
  faEllipsisVertical,
  faAddressCard,
} from "@fortawesome/free-solid-svg-icons";
import { useToast } from "providers/ToastProvider";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { EmpresaType } from "../editarUsuarios/userTypes";
// Import the InformacoesUsuario component
import InformacoesUsuario from "../editarUsuarios/InformacoesUsuario";
// Import UnsavedChangesModal from common components to maintain consistency
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import Badge from "components/base/Badge";
import { usePermissionModal } from "contexts/PermissionModalContext";
import PermissoesPdv from "../editarUsuarios/permissoesPdv";
import AcessoAoSistema from "../editarUsuarios/acessoAoSistema";

// Define breadcrumbs for the new user page
const usuarioBreadcrumbItems = () => [
  {
    label: "Retaguarda",
    url: "#!",
  },
  {
    label: "Cadastros",
    url: "#!",
  },
  {
    label: "Usuários",
    url: "/cadastros/usuarios",
  },
  {
    label: "Novo",
    active: true,
  },
];

// Define API response type
interface ApiResponse<T> {
  data: T;
  isSuccess: boolean;
  message: string;
}

// Define form values interface for the new user
interface FormValues {
  nome: string;
  nomeCompleto: string;
  email: string;
  senha: string;
  confirmarSenha: string;
  novaSenha?: string; // Added for compatibility with InformacoesUsuario
  telefone?: string;
  cpf?: string;
  crf?: string;
  grupoId?: number;
  isVendedor?: boolean;
  isEntregador?: boolean;
  isSngpc?: boolean;
  isSemAcesso?: boolean;
  fpUsuario?: string;
  fpSenha?: string;
  pdvUsuario?: string;
  pdvSenha?: string;
  storeAccess?: Array<{
    empresaId: number;
    hasAccess: boolean;
    isWorkLocation: boolean;
  }>;
  empresasAssociadas?: Array<{
    empresaId: number;
    local: boolean;
  }>;
  imageUrl?: string;
  codigo?: string; // Added for compatibility with InformacoesUsuario
  descontoMaximo?: boolean;
  percentualDescontoMaximo?: number | null;
  visualizarEstoque?: boolean;
  liberarCrediario?: boolean;
  precoCusto?: boolean;
  acessaRetaguarda: boolean;
  alternaBalcaoCaixa: boolean;
  acessoAdm: boolean;
  cancelaVenda: boolean;
  visulizarDetalhesCaixa: boolean;
  abrirCaixa: boolean;
}

const NovoUsuario = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Usar o contexto para controlar o modal de permissão
  const { getMutationErrorHandler, getQueryRetryHandler } =
    usePermissionModal();

  const [showPdvPassword, setShowPdvPassword] = useState(false);
  const [showFpPassword, setShowFpPassword] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const [formInitialized, setFormInitialized] = useState(false);
  const [formDirty, setFormDirty] = useState(false);

  // Initialize form with default values
  const formMethods = useForm<FormValues>({
    defaultValues: {
      nome: "",
      nomeCompleto: "",
      email: "",
      senha: "",
      confirmarSenha: "",
      novaSenha: "", // For compatibility with InformacoesUsuario
      telefone: "",
      cpf: "",
      crf: "",
      grupoId: undefined,
      isVendedor: false,
      isEntregador: false,
      isSngpc: false,
      isSemAcesso: false,
      fpUsuario: "",
      fpSenha: "",
      pdvUsuario: "",
      pdvSenha: "",
      storeAccess: [],
      empresasAssociadas: [],
      codigo: "", // For compatibility with InformacoesUsuario
      imageUrl: "",
      descontoMaximo: false,
      percentualDescontoMaximo: null,
      visualizarEstoque: false,
      liberarCrediario: false,
      precoCusto: false,
      acessaRetaguarda: false,
      alternaBalcaoCaixa: false,
      acessoAdm: false,
      cancelaVenda: false,
      visulizarDetalhesCaixa: false,
      abrirCaixa: false,
    },
  });

  // Mark form as initialized after initial render
  useEffect(() => {
    // Initialize the form and ensure it starts as "clean"
    formMethods.reset(formMethods.getValues(), {
      keepDirty: false,
      keepTouched: false,
      keepIsValid: true,
      keepIsSubmitted: false,
      keepSubmitCount: false,
    });
    setFormDirty(false);
    setFormInitialized(true);
  }, []);

  // Track form changes
  useEffect(() => {
    if (!formInitialized) return;

    // Watch all form changes
    const subscription = formMethods.watch((value, { name, type }) => {
      if (!formDirty) {
        setFormDirty(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [formMethods, formDirty, formInitialized]);

  // Reference to track whether form is dirty
  const isDirtyRef = useRef(false);

  useEffect(() => {
    isDirtyRef.current = formMethods.formState.isDirty;
  }, [formMethods.formState.isDirty]);

  // Setup navigation blocker
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block if form is not initialized yet
    if (!formInitialized) return false;

    // Don't block if we're submitting the form or if it was already successful
    if (
      formMethods.formState.isSubmitting ||
      (mutation.isSuccess && mutation.data?.isSuccess)
    ) {
      return false;
    }

    // Check for real changes in the form
    const hasRealChanges =
      Object.keys(formMethods.formState.dirtyFields).length > 0;

    // Check if any fields are filled
    const hasFilledFields =
      !!formMethods.getValues().nome ||
      !!formMethods.getValues().email ||
      !!formMethods.getValues().senha;

    const shouldBlock =
      (hasRealChanges || formDirty) &&
      hasFilledFields &&
      currentLocation.pathname !== nextLocation.pathname;

    if (shouldBlock) {
      setPendingNavigation(nextLocation.pathname);
      setShowNavigationModal(true);
    }

    return shouldBlock;
  });

  // Function to handle navigation confirmation
  const handleNavigationConfirm = () => {
    setShowNavigationModal(false);

    if (pendingNavigation) {
      // Reset dirty state before navigating
      isDirtyRef.current = false;

      // Unblock and navigate
      blocker.proceed?.();
      navigate(pendingNavigation);
    }
  };

  // Function to cancel navigation
  const handleNavigationCancel = () => {
    setShowNavigationModal(false);
    blocker.reset?.();
  };

  // Query to fetch empresas (stores/companies)
  const empresasQuery = useQuery<{ itens: EmpresaType[] }>({
    queryKey: ["empresas"],
    queryFn: async () => {
      const response = await axios.get("/api/empresa");
      return response.data;
    },
    retry: getQueryRetryHandler({
      queryKey: ["empresas"],
      defaultMessage: "Você não possui permissão para consultar lojas.",
    }),
  });

  // Mutation for creating a new user
  const mutation = useMutation<ApiResponse<any>, any, FormValues>({
    mutationFn: async (formData) => {
      // Create payload for API
      const empresas = formData.storeAccess
        ?.filter((sa) => sa.hasAccess)
        .map((sa) => ({
          EmpresaId: sa.empresaId,
          Local: sa.isWorkLocation || false,
        }));

      // Prepare the request data - use senha or novaSenha (from InformacoesUsuario)
      const requestData = {
        nome: formData.nome,
        nomeCompleto: formData.nomeCompleto,
        email: formData.email,
        senha: formData.senha || formData.novaSenha, // Use either senha or novaSenha
        telefone: formData.telefone,
        cpf: formData.cpf,
        crf: formData.crf,
        grupoId: formData.grupoId,
        isVendedor: formData.isVendedor || false,
        isEntregador: formData.isEntregador || false,
        isSngpc: formData.isSngpc || false,
        isSemAcesso: formData.isSemAcesso || false,
        descontoMaximo: formData.descontoMaximo || false,
        percentualDescontoMaximo: formData.percentualDescontoMaximo ?? null,
        visualizarEstoque: formData.visualizarEstoque || false,
        liberarCrediario: formData.liberarCrediario || false,
        precoCusto: formData.precoCusto || false,
        fpUsuario: formData.fpUsuario || "",
        fpSenha: formData.fpSenha || "",
        pdvUsuario: formData.pdvUsuario || "",
        pdvSenha: formData.pdvSenha || "",
        empresasAssociadas: empresas || [],
        imageUrl: formData.imageUrl || "",
        acessaRetaguarda: formData.acessaRetaguarda,
        alternaBalcaoCaixa: formData.alternaBalcaoCaixa,
        acessoAdm: formData.acessoAdm,
        cancelaVenda: formData.cancelaVenda,
        visulizarDetalhesCaixa: formData.visulizarDetalhesCaixa,
        abrirCaixa: formData.abrirCaixa
      };

      const response = await axios.post("/api/usuario", requestData);
      return response.data;
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para cadastrar usuários",
      (error) => {
        console.error("Error creating user:", error);
        showToast(
          error.response?.data?.message || "Erro ao criar usuário",
          "error"
        );
      }
    ),
    onSuccess: (response) => {
      if (response.isSuccess) {
        // Reset form dirty state
        setFormDirty(false);
        isDirtyRef.current = false;

        const codigo = response.data?.codigo || "";
        navigate("/cadastros/usuarios", {
          state: {
            successCode: codigo,
            isNew: true,
            timestamp: new Date().getTime(),
          },
        });
      } else {
        showToast(response.message || "Erro ao criar usuário", "error");
      }
    },
  });

  useEffect(() => {
    if (mutation.isSuccess && mutation.data?.isSuccess) {
      setFormDirty(false);
    }
  }, [mutation.isSuccess, mutation.data?.isSuccess]);

  // Handle store access functions
  const getStoreAccess = (empresaId: number) => {
    const storeAccess = formMethods.watch("storeAccess") || [];
    return (
      storeAccess.find((sa) => sa.empresaId === empresaId) || {
        empresaId,
        hasAccess: false,
        isWorkLocation: false,
      }
    );
  };

  const updateStoreAccess = (
    empresaId: number,
    hasAccess: boolean,
    isWorkLocation: boolean = false
  ) => {
    // Get current storeAccess from form
    const currentStoreAccess = formMethods.getValues("storeAccess") || [];
    let newStoreAccess = [...currentStoreAccess];

    if (isWorkLocation) {
      // If setting a new work location, remove that flag from all others
      newStoreAccess = newStoreAccess.map((sa) => ({
        ...sa,
        isWorkLocation: sa.empresaId === empresaId ? isWorkLocation : false,
      }));
    }

    const existingIndex = newStoreAccess.findIndex(
      (sa) => sa.empresaId === empresaId
    );
    if (existingIndex >= 0) {
      // Update existing entry
      newStoreAccess[existingIndex] = {
        empresaId,
        hasAccess,
        isWorkLocation:
          isWorkLocation || newStoreAccess[existingIndex].isWorkLocation,
      };
    } else {
      // Add new entry
      newStoreAccess.push({ empresaId, hasAccess, isWorkLocation });
    }

    // Update form values directly
    formMethods.setValue("storeAccess", newStoreAccess, { shouldDirty: true });

    // Update selectedEmpresas for backward compatibility
    const updatedEmpresas = newStoreAccess
      .filter((sa) => sa.hasAccess)
      .map((sa) => sa.empresaId);

    // Update empresasAssociadas with the correct format
    formMethods.setValue(
      "empresasAssociadas",
      newStoreAccess
        .filter((sa) => sa.hasAccess)
        .map((sa) => ({
          empresaId: sa.empresaId,
          local: sa.isWorkLocation,
        })),
      { shouldDirty: true }
    );
  };

  const toggleStoreAccess = (empresaId: number) => {
    const access = getStoreAccess(empresaId);
    updateStoreAccess(
      empresaId,
      !access.hasAccess,
      access.isWorkLocation && !access.hasAccess
    );
  };

  const setAsWorkLocation = (empresaId: number) => {
    updateStoreAccess(empresaId, true, true);
  };

  const toggleDropdown = (empresaId: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setOpenDropdownId(openDropdownId === empresaId ? null : empresaId);
  };

  // Password visibility toggles
  const togglePdvPasswordVisibility = () => {
    setShowPdvPassword(!showPdvPassword);
  };

  const toggleFpPasswordVisibility = () => {
    setShowFpPassword(!showFpPassword);
  };

  // Form validation functions
  const validatePasswords = () => {
    const senha =
      formMethods.getValues("senha") || formMethods.getValues("novaSenha");
    const confirmarSenha = formMethods.getValues("confirmarSenha");

    if (!senha) {
      formMethods.setError("senha", {
        type: "required",
        message: "A senha é obrigatória",
      });
      return false;
    }

    if (senha !== confirmarSenha) {
      formMethods.setError("confirmarSenha", {
        type: "validate",
        message: "As senhas não correspondem",
      });
      return false;
    }

    return true;
  };

  const validateWorkLocation = () => {
    const storeAccess = formMethods.getValues("storeAccess") || [];
    const hasWorkLocation = storeAccess.some(
      (sa) => sa.hasAccess && sa.isWorkLocation
    );

    if (
      storeAccess.filter((sa) => sa.hasAccess).length > 0 &&
      !hasWorkLocation
    ) {
      showToast(
        "É necessário definir pelo menos uma empresa como local de trabalho",
        "error"
      );
      return false;
    }

    return true;
  };

  const validateForm = () => {
    let isValid = true;

    // Required fields validation
    const requiredFields = [
      { name: "nome", message: "O nome de usuário é obrigatório" },
      { name: "nomeCompleto", message: "O nome completo é obrigatório" },
      { name: "email", message: "O email é obrigatório" },
      { name: "grupoId", message: "O grupo de usuário é obrigatório" },
    ];

    requiredFields.forEach((field) => {
      if (!formMethods.getValues(field.name as keyof FormValues)) {
        formMethods.setError(field.name as any, {
          type: "required",
          message: field.message,
        });
        isValid = false;
      }
    });

    // Email validation
    const email = formMethods.getValues("email");
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (email && !emailRegex.test(email)) {
      formMethods.setError("email", {
        type: "pattern",
        message: "Email inválido",
      });
      isValid = false;
    }

    // Password validation
    if (!validatePasswords()) {
      isValid = false;
    }

    // CPF is mandatory for SNGPC users
    if (formMethods.getValues("isSngpc") && !formMethods.getValues("cpf")) {
      formMethods.setError("cpf", {
        type: "required",
        message: "CPF é obrigatório para Responsáveis Técnicos SNGPC",
      });
      isValid = false;
    }

    // Validate at least one work location if stores are associated
    if (!validateWorkLocation()) {
      isValid = false;
    }

    return isValid;
  };

  // Form submission handler
  const handleSubmit: SubmitHandler<FormValues> = (formData) => {
    if (!validateForm()) {
      return;
    }

    // Sync novaSenha (from InformacoesUsuario) with senha
    if (formData.novaSenha && !formData.senha) {
      formData.senha = formData.novaSenha;
    } else if (formData.senha && !formData.novaSenha) {
      formData.novaSenha = formData.senha;
    }

    // Clear the form's dirty state
    formMethods.reset(formData, { keepValues: true });
    setFormDirty(false);

    mutation.mutate(formData);
  };

  // Format CNPJ for display
  const formatCnpj = (cnpj: string) => {
    if (!cnpj) return "";
    return cnpj.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  };

  return (
    <div>
      {/* Replace custom modal with the shared UnsavedChangesModal */}
      <UnsavedChangesModal
        show={showNavigationModal}
        onConfirm={handleNavigationConfirm}
        onCancel={handleNavigationCancel}
      />

      <PageBreadcrumb items={usuarioBreadcrumbItems()} />

      <FormProvider {...formMethods}>
        <Form
          noValidate
          className="mb-9"
          onSubmit={formMethods.handleSubmit(handleSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Cadastrar um Usuário</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Preencha as informações do novo usuário
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="primary"
                type="submit"
                disabled={mutation.isPending}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Salvar Usuário
              </Button>
            </div>
          </div>

          {/* User Information Card - Using InformacoesUsuario component */}
          <InformacoesUsuario
            isNewUser={true} // Pass this prop to indicate it's a new user form
            imageEndpoint={`/api/usuario/imagem`} // This is only used for existing users
          />

          {/* Permissões e Configurações Card */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faAddressCard} className="me-2" />
                Permissões e Configurações
              </h5>
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex flex-column gap-3">
                    <div className="border-start border-4 ps-3 mb-3">
                      <AcessoAoSistema />
                    </div>

                    <div className="border-start border-4 ps-3">
                      <h6 className="fw-bold mb-3">Funções do Usuário</h6>
                      <Form.Check
                        type="switch"
                        id="isEntregador"
                        label="Permite selecionar usuário como entregador"
                        className="mb-2"
                        {...formMethods.register("isEntregador")}
                      />
                      <Form.Check
                        type="switch"
                        id="isVendedor"
                        label="Permite selecionar usuário como vendedor"
                        className="mb-2"
                        {...formMethods.register("isVendedor")}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="border-start border-4 ps-3">
                    <PermissoesPdv disabled={false} />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Dados para acesso à Farmácia Popular Card */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                Dados para acesso à Farmácia Popular
              </h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="farmaciaPopularUsuario">
                    <Form.Label>Usuário Farmácia Popular</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Nome de usuário"
                      {...formMethods.register("fpUsuario")}
                    />
                    <Form.Text className="text-muted">
                      Usuário de acesso ao sistema Farmácia Popular
                    </Form.Text>
                  </Form.Group>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="farmaciaPopularSenha">
                    <Form.Label>Senha Farmácia Popular</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showFpPassword ? "text" : "password"}
                        placeholder="Senha para Farmácia Popular"
                        {...formMethods.register("fpSenha")}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={toggleFpPasswordVisibility}
                        className="rounded-end"
                      >
                        <FontAwesomeIcon
                          icon={showFpPassword ? faEyeSlash : faEye}
                        />
                      </Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Senha para acesso ao sistema Farmácia Popular
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Dados para acesso do PDV Card */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                Dados para acesso do PDV
              </h5>
              <div className="row g-3">
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="pdvUsuario">
                    <Form.Label>Usuário PDV</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Nome de usuário para o PDV"
                      {...formMethods.register("pdvUsuario")}
                    />
                    <Form.Text className="text-muted">
                      Nome de usuário utilizado para acesso ao PDV
                    </Form.Text>
                  </Form.Group>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Group controlId="pdvSenha">
                    <Form.Label>Senha PDV</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPdvPassword ? "text" : "password"}
                        placeholder="Senha para o PDV"
                        {...formMethods.register("pdvSenha")}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={togglePdvPasswordVisibility}
                        className="rounded-end"
                      >
                        <FontAwesomeIcon
                          icon={showPdvPassword ? faEyeSlash : faEye}
                        />
                      </Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Senha para acesso ao PDV
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Lojas Associadas Card */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faStore} className="me-2" />
                Lojas Associadas
              </h5>

              {empresasQuery.isLoading ? (
                <div className="text-center py-4">
                  <Spinner
                    animation="border"
                    role="status"
                    size="sm"
                    className="me-2"
                  />
                  <span>Carregando lojas...</span>
                </div>
              ) : empresasQuery.error ? (
                (() => {
                  const error = empresasQuery.error as any;
                  if (
                    axios.isAxiosError(error) &&
                    error.response?.status === 403
                  ) {
                    return (
                      <AccessDeniedOverlay
                        message="Você não tem permissão para consultar ou visualizar lojas."
                        compact
                      >
                        <div
                          style={{
                            filter: "blur(8px)",
                            pointerEvents: "none",
                            userSelect: "none",
                            minHeight: "100px",
                          }}
                        >
                          <p className="mb-2">Lojas selecionadas (0):</p>
                          <div className="d-flex flex-wrap gap-2 ms-3">
                            {Array(5)
                              .fill(0)
                              .map((_, i) => (
                                <Badge
                                  key={i}
                                  bg="primary"
                                  className="d-flex align-items-center py-1 px-2"
                                  style={{ borderRadius: "8px" }}
                                >
                                  Loja Exemplo
                                </Badge>
                              ))}
                          </div>
                        </div>
                      </AccessDeniedOverlay>
                    );
                  }
                  return (
                    <div className="alert alert-danger">
                      Erro ao carregar as lojas. Por favor, tente novamente.
                    </div>
                  );
                })()
              ) : (
                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                  {empresasQuery.data?.itens.map((empresa) => {
                    const access = getStoreAccess(empresa.id);
                    const hasAccess = access.hasAccess;
                    const isCurrentWorkLocation = access.isWorkLocation;
                    const isDropdownOpen = openDropdownId === empresa.id;

                    return (
                      <div className="col" key={empresa.id}>
                        <div
                          className={`card h-100 border ${
                            hasAccess
                              ? isCurrentWorkLocation
                                ? "border-success"
                                : "border-primary"
                              : "border-light"
                          } position-relative`}
                          style={{
                            transition: "all 0.2s ease-in-out",
                            transform: hasAccess ? "scale(1.02)" : "scale(1)",
                            cursor: "pointer", 
                            overflow: "visible",
                          }}
                          onClick={(e) => toggleDropdown(empresa.id, e)}
                        >
                          <Dropdown
                            className="position-absolute"
                            style={{ top: "10px", right: "10px" }}
                            show={isDropdownOpen}
                            onToggle={() => toggleDropdown(empresa.id)}
                          >
                            <Dropdown.Toggle
                              as="div"
                              className="btn btn-sm btn-light rounded-circle"
                              style={{
                                width: "32px",
                                height: "32px",
                                padding: "0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                visibility: isDropdownOpen
                                  ? "visible"
                                  : "hidden",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FontAwesomeIcon icon={faEllipsisVertical} />
                            </Dropdown.Toggle>

                            <Dropdown.Menu
                              align="end"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Dropdown.Item
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleStoreAccess(empresa.id);
                                  setOpenDropdownId(null);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={hasAccess ? faLock : faUnlock}
                                  className={`me-2 ${
                                    hasAccess ? "text-danger" : "text-primary"
                                  }`}
                                />
                                {hasAccess
                                  ? "Remover acesso"
                                  : "Permitir acesso"}
                              </Dropdown.Item>

                              <Dropdown.Item
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAsWorkLocation(empresa.id);
                                  setOpenDropdownId(null);
                                }}
                                disabled={isCurrentWorkLocation}
                              >
                                <FontAwesomeIcon
                                  icon={faBriefcase}
                                  className="me-2 text-success"
                                />
                                {isCurrentWorkLocation
                                  ? "Local de trabalho atual"
                                  : "Definir como local de trabalho"}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>

                          {hasAccess && (
                            <div
                              className={`position-absolute d-flex justify-content-center align-items-center ${
                                isCurrentWorkLocation
                                  ? "bg-success"
                                  : "bg-primary"
                              }`}
                              style={{
                                top: "-10px",
                                left: "-10px",
                                width: "28px",
                                height: "28px",
                                borderRadius: "50%",
                                color: "white",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={
                                  isCurrentWorkLocation ? faBriefcase : faUnlock
                                }
                                size="sm"
                              />
                            </div>
                          )}

                          <div className="card-body d-flex flex-column">
                            <div className="d-flex align-items-center mb-3">
                              <div
                                className={`d-flex justify-content-center align-items-center me-3 ${
                                  hasAccess ? "" : "text-muted"
                                }`}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "8px",
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={hasAccess ? faBuilding : faLock}
                                  size="lg"
                                />
                              </div>
                              <div>
                                <h6 className="fw-bold mb-0">
                                  {empresa.nomeFantasia}
                                  {isCurrentWorkLocation && (
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip>Local de trabalho</Tooltip>
                                      }
                                    >
                                      <span className="ms-2 badge bg-success">
                                        <FontAwesomeIcon
                                          icon={faBriefcase}
                                          size="xs"
                                        />
                                      </span>
                                    </OverlayTrigger>
                                  )}
                                </h6>
                                <small>{formatCnpj(empresa.cnpj)}</small>
                              </div>
                            </div>

                            <div className="mt-2 pt-2 border-top">
                              <div className="d-flex align-items-start">
                                <FontAwesomeIcon
                                  icon={faMapMarkerAlt}
                                  className={`mt-1 me-2 ${
                                    hasAccess
                                      ? "text-primary"
                                      : "text-secondary"
                                  }`}
                                />
                                <div>
                                  <p className="mb-1 small">
                                    {empresa.endereco?.logradouro},{" "}
                                    {empresa.endereco?.numero}
                                    {empresa.endereco?.complemento
                                      ? ` - ${empresa.endereco?.complemento}`
                                      : ""}
                                  </p>
                                  <p className="mb-1 small">
                                    {empresa.endereco?.bairro}
                                  </p>
                                  <p className="mb-0 small">
                                    {empresa.endereco?.municipio} -{" "}
                                    {empresa.endereco?.uf},{" "}
                                    {empresa.endereco?.cep}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {empresasQuery.data?.itens?.length === 0 && (
                <div className="alert alert-info">
                  Nenhuma loja disponível no momento.
                </div>
              )}
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate("/cadastros/usuarios")}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={mutation.isPending}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              {mutation.isPending ? "Salvando..." : "Salvar Usuário"}
            </Button>
          </div>
        </Form>
      </FormProvider>
    </div>
  );
};

export default NovoUsuario;
